/**
 * Equivalent FR <-> EN paths for every page that has a localized slug.
 *
 * The language switcher cannot simply add/remove the "/en" prefix because many
 * pages use a different slug per language (e.g. "/application-recus" in French
 * but "/en/receipt-app" in English). Swapping only the prefix would land on a
 * non-existent route and trigger a 404.
 *
 * Keep this list in sync with the routes declared in AppRoutes.tsx.
 * Paths must NOT include a trailing slash.
 */
import { jsonPosts } from "@/lib/blog/registry";

export const ROUTE_PAIRS: Array<{ fr: string; en: string }> = [
  ...jsonPosts.map((post) => ({ fr: post.frPath, en: post.enPath })),
  { fr: "/", en: "/en" },
  { fr: "/privacy", en: "/en/privacy" },
  { fr: "/terms", en: "/en/terms" },
  { fr: "/support", en: "/en/support" },
  // Demo video page (direct link for press and Apple, not in the nav)
  { fr: "/demo", en: "/en/demo" },
  // Suppression de compte (exigence Google Play)
  { fr: "/supprimer-compte", en: "/en/delete-account" },
  // Meta-ads landing page (parents / families)
  { fr: "/parents-recus-retours", en: "/en/parents-receipts-returns" },
  // Meta-ads landing page (home reno / DIY)
  { fr: "/reno-recus-retours", en: "/en/home-reno-receipts-returns" },
  // SEO pillar content pages
  { fr: "/application-recus", en: "/en/receipt-app" },
  { fr: "/application-retours-magasin", en: "/en/returns-app" },
  { fr: "/application-garanties", en: "/en/warranty-tracker-app" },
  { fr: "/application-recus-famille", en: "/en/family-receipt-app" },
  // Feature pages (pillar-style)
  { fr: "/fonctionnalites/precision-ocr", en: "/en/features/ocr-accuracy" },
  { fr: "/fonctionnalites/stockage-cloud-securise", en: "/en/features/secure-cloud-storage" },
  { fr: "/fonctionnalites/categorisation-filtres", en: "/en/features/categories-filters" },
  // Blog listing
  { fr: "/blog", en: "/en/blog" },
  // Articles
  { fr: "/blog/meilleure-app-recu-2026", en: "/en/blog/meilleure-app-recu-2026" },
  {
    fr: "/blog/comment-numeriser-recus-application-mobile",
    en: "/en/blog/comment-numeriser-recus-application-mobile",
  },
  { fr: "/blog/recu-efface-quoi-faire", en: "/en/blog/faded-receipt-what-to-do" },
  {
    fr: "/blog/comment-conserver-recus-garanties",
    en: "/en/blog/how-to-keep-receipts-for-warranties",
  },
  {
    fr: "/blog/garantie-magasin-vs-garantie-manufacturier",
    en: "/en/blog/store-warranty-vs-manufacturer-warranty",
  },
  {
    fr: "/blog/comment-faire-retour-magasin-avec-telephone",
    en: "/en/blog/how-to-return-items-in-store-with-your-phone",
  },
  { fr: "/blog/meilleure-application-recus-canada", en: "/en/blog/best-receipt-app-canada" },
  {
    fr: "/blog/partager-recu-conjoint-famille",
    en: "/en/blog/share-receipt-with-partner-family",
  },
  {
    fr: "/blog/comment-choisir-application-recus",
    en: "/en/blog/how-to-choose-receipt-app",
  },
];

const FR_TO_EN = new Map(ROUTE_PAIRS.map((p) => [p.fr, p.en]));
const EN_TO_FR = new Map(ROUTE_PAIRS.map((p) => [p.en, p.fr]));

/**
 * Returns the equivalent path in the other language for a given pathname.
 * Falls back to a naive "/en" prefix swap for paths not in the map (e.g.
 * dynamic routes that share the same slug across languages).
 */
export function getEquivalentPath(pathname: string, currentLanguage: "fr" | "en"): string {
  // Normalize: drop trailing slash (except for the root "/").
  const path = pathname !== "/" ? pathname.replace(/\/$/, "") : pathname;

  if (currentLanguage === "en") {
    const fr = EN_TO_FR.get(path);
    if (fr) return fr;
    // Fallback: strip the /en prefix.
    const stripped = path.replace(/^\/en\/?/, "/");
    return stripped || "/";
  }

  const en = FR_TO_EN.get(path);
  if (en) return en;
  // Fallback: add the /en prefix.
  return path === "/" ? "/en" : `/en${path}`;
}
